const EXPANDED_CLASS = 'scope-rail--expanded'
const TRANSITIONING_CLASS = 'scope-rail-transitioning'
const DRAG_THRESHOLD = 40
const HORIZONTAL_ABORT = 8
const RUBBER_BAND_MAX = 12
const NAVIGATION_FEEDBACK_TIMEOUT = 8000

let navigationFeedbackTimer

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

let drag = null

function setExpanded(rail, expanded) {
  if (rail.classList.contains(EXPANDED_CLASS) === expanded) return
  const apply = () => {
    rail.classList.toggle(EXPANDED_CLASS, expanded)
    rail
      .querySelector('[data-scope-rail-toggle]')
      ?.setAttribute('aria-expanded', String(expanded))
  }
  if (typeof document.startViewTransition !== 'function' || reduceMotion.matches) {
    apply()
    return
  }
  document.documentElement.classList.add(TRANSITIONING_CLASS)
  document
    .startViewTransition(apply)
    .finished.finally(() =>
      document.documentElement.classList.remove(TRANSITIONING_CLASS)
    )
}

function onPointerDown(event) {
  if (event.pointerType === 'mouse') return
  const row = event.target instanceof Element && event.target.closest('.scope-rail__row')
  const rail = row instanceof HTMLElement && row.closest('[data-scope-rail]')
  if (!(row instanceof HTMLElement) || !(rail instanceof HTMLElement)) return
  drag = {
    rail,
    row,
    id: event.pointerId,
    x: event.clientX,
    y: event.clientY,
    lastY: event.clientY,
    expanded: rail.classList.contains(EXPANDED_CLASS),
    expandable: window.scrollY === 0,
    scrolling: false,
  }
}

/**
 * `touch-action: pan-x` keeps vertical touches for the pull, so the browser will
 * not scroll the page from here; other vertical moves scroll it by hand.
 */
function onPointerMove(event) {
  if (!drag || event.pointerId !== drag.id) return
  const dx = event.clientX - drag.x
  const dy = event.clientY - drag.y
  const step = event.clientY - drag.lastY
  drag.lastY = event.clientY
  if (drag.scrolling) {
    window.scrollBy({ top: -step, behavior: 'instant' })
    return
  }
  if (Math.abs(dx) > HORIZONTAL_ABORT && Math.abs(dx) > Math.abs(dy)) {
    endDrag()
    return
  }
  const pull = drag.expanded ? -dy : dy
  const pullIsOurs = drag.expanded || drag.expandable
  if (pull < -HORIZONTAL_ABORT || (!pullIsOurs && pull > HORIZONTAL_ABORT)) {
    drag.row.style.translate = ''
    drag.scrolling = true
    window.scrollBy({ top: -step, behavior: 'instant' })
    return
  }
  if (pull >= DRAG_THRESHOLD) {
    const { rail, expanded } = drag
    endDrag()
    setExpanded(rail, !expanded)
    return
  }
  if (reduceMotion.matches) return
  const band = Math.max(0, Math.min(pull * 0.3, RUBBER_BAND_MAX))
  drag.row.style.translate = `0 ${drag.expanded ? -band : band}px`
}

function endDrag() {
  if (!drag) return
  drag.row.style.translate = ''
  drag = null
}

function onClick(event) {
  const target = event.target
  if (!(target instanceof Element)) return
  const toggle = target.closest('[data-scope-rail-toggle]')
  const rail = toggle?.closest('[data-scope-rail]')
  if (toggle && rail instanceof HTMLElement) {
    setExpanded(rail, !rail.classList.contains(EXPANDED_CLASS))
    return
  }
  for (const open of document.querySelectorAll(`.${EXPANDED_CLASS}`)) {
    if (open instanceof HTMLElement && !open.contains(target)) setExpanded(open, false)
  }
}

function onKeydown(event) {
  if (event.key !== 'Escape') return
  clearPendingNavigation()
  for (const open of document.querySelectorAll(`.${EXPANDED_CLASS}`)) {
    if (open instanceof HTMLElement) setExpanded(open, false)
  }
}

function clearPendingNavigation() {
  clearTimeout(navigationFeedbackTimer)
  document.querySelector('[data-scope-link][aria-busy="true"]')?.removeAttribute('aria-busy')
}

function showPendingNavigation(event) {
  if (event.defaultPrevented || event.button !== 0) return
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
  const link = event.target instanceof Element && event.target.closest('a[data-scope-link]')
  if (!(link instanceof HTMLAnchorElement) || link.href === location.href) return

  clearPendingNavigation()
  link.setAttribute('aria-busy', 'true')
  navigationFeedbackTimer = setTimeout(clearPendingNavigation, NAVIGATION_FEEDBACK_TIMEOUT)
}

document.addEventListener('pointerdown', onPointerDown, { passive: true })
document.addEventListener('pointermove', onPointerMove, { passive: true })
document.addEventListener('pointerup', endDrag, { passive: true })
document.addEventListener('pointercancel', endDrag, { passive: true })
document.addEventListener('click', onClick)
document.addEventListener('keydown', onKeydown)

window.addEventListener('click', showPendingNavigation)
window.addEventListener('pageshow', clearPendingNavigation)
